import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import { LoadingView } from '@quyva/shared-ui';
import { repoStatsDbConfig, getDisplayDateTimeParts, RepoStatKey, getRepoStatLabel } from '@quyva/common';
import { Tabs } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { getUrl } from './pdfDisplay/utils';
import { useGlobalCredits } from '../context/GlobalStateContext.js';
import { DiagnosticsPanel } from './diagnostic_data';


export const RepoStatsDB = ({ repoName, email }) => {
    const [tablesData, setTablesData] = useState([]);
    const [dbStats, setDbStats] = useState(null);
    const [activeTab, setActiveTab] = useState('token-diagnostics');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { isAdmin } = useGlobalCredits();

    const getDatabaseStats = async () => {
        try {
            setIsLoading(true);
            const apiUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/database-stats?repo_name=${repoName}&user_email=${email}`
            );
            const response = await fetch(apiUrl);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            if (Array.isArray(data?.tables)) {
                setTablesData(data.tables);
                setDbStats(null);
            } else {
                setDbStats(data);
                setTablesData([]);
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error.message);
            setErrorMessage(error.message);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getDatabaseStats();
    }, [repoName]);

    if (isLoading) {
        return (
            <div className='w-full h-96 max-h-96 flex justify-center items-center'>
                <LoadingView loadingText={'Loading database stats ...'} />
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className='w-full p-3 h-96 max-h-96 overflow-auto flex flex-col justify-center items-center text-buttons-alert400 font-semibold'>
                <FontAwesomeIcon icon={faExclamationTriangle} size='2x' />
                <div className='mt-2'>{errorMessage}</div>
            </div>
        );
    }

    const formatDisplayDateTime = (timestamp) => {
        if (!timestamp) return '—';

        const { date, time } = getDisplayDateTimeParts(timestamp);
        return date && time ? `${date}, ${time}` : '—';
    };

    const summaryItemMap = dbStats
        ? {
            [RepoStatKey.TOTAL_TABLES]: {
                label: getRepoStatLabel(RepoStatKey.TOTAL_TABLES),
                value: dbStats.totalTables ?? '—',
            },
            [RepoStatKey.SCHEMA_SIZE_KB]: {
                label: getRepoStatLabel(RepoStatKey.SCHEMA_SIZE_KB),
                value: dbStats.schemaSizeKb?.toFixed(2) ?? '—',
            },
            [RepoStatKey.MANUAL_ANNOTATION]: {
                label: getRepoStatLabel(RepoStatKey.MANUAL_ANNOTATION),
                value: dbStats.manualAnnotation ? 'True' : 'False',
            },
            [RepoStatKey.CREATED_AT]: {
                label: getRepoStatLabel(RepoStatKey.CREATED_AT),
                value: formatDisplayDateTime(dbStats.createdAt),
            },
            [RepoStatKey.UPDATED_AT]: {
                label: getRepoStatLabel(RepoStatKey.UPDATED_AT),
                value: formatDisplayDateTime(dbStats.updatedAt),
            },
            [RepoStatKey.PROCESSING_TIME]: {
                label: getRepoStatLabel(RepoStatKey.PROCESSING_TIME),
                value: dbStats.processingTime !== undefined && dbStats.processingTime !== null
                    ? `${dbStats.processingTime.toFixed(2)} s`
                    : '—',
            },
            [RepoStatKey.EMBEDDING_MODEL]: {
                label: getRepoStatLabel(RepoStatKey.EMBEDDING_MODEL),
                value: dbStats.embeddingModel ?? '—',
            },
            [RepoStatKey.USED_CREDITS]: {
                label: getRepoStatLabel(RepoStatKey.USED_CREDITS),
                value: dbStats.usedCredits ?? '—',
            },
        }
        : {};


    const displayConfig = isAdmin ? repoStatsDbConfig.admin : repoStatsDbConfig.user;

    const columns = displayConfig.map((row) =>
        row
            .map((key) => summaryItemMap[key])
            .filter(
                (item) =>
                    item &&
                    item.value !== null &&
                    item.value !== undefined &&
                    String(item.value).trim() !== '' &&
                    item.value !== '—'
            )
    );


    return (
        <div className='w-[90vw] min-w-[90px] md:w-[85vw] md:min-w-[85vw] flex-col px-6 py-6 rounded-xl bg-backgrounds-slate'>
            {dbStats ? (
                <>
                    <div className="w-full bg-white rounded-lg border p-3 mb-3">
                        <div className="flex flex-col md:flex-row gap-3 w-full">
                            {columns.map((colItems, colIdx) => (
                                <div key={colIdx} className="flex flex-col gap-2 w-full md:flex-1">
                                    {colItems.map((item) => (
                                        <div
                                            key={item.label}
                                            className="px-3 py-2 rounded-md border bg-backgrounds-secondary flex items-center justify-between gap-2"
                                        >
                                            <div className="text-sm font-medium text-texts-secondary300">
                                                {item.label}
                                            </div>
                                            <div className="text-right">{item.value}</div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {isAdmin && (
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            className='h-full repository-stats-tabs'
                            items={[
                                {
                                    key: 'token-diagnostics',
                                    label: (
                                        <span>
                                            <FontAwesomeIcon icon={faGaugeHigh} className='mr-2' />
                                            Token Diagnostics
                                        </span>
                                    ),
                                    children: (
                                        <DiagnosticsPanel diagnostics={dbStats?.display_diagnostics} wrapperClassName='min-h-0 pr-1' />
                                    ),
                                },
                            ]}
                        />
                    )}
                </>
            ) : (
                <p>No stats available for this repository</p>
            )}


        </div>
    );
};
